import { createFileRoute, Navigate } from '@tanstack/react-router'
import { ClockIcon, MailIcon, MoreHorizontal, UserIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIdempotencyKey } from '@/api/idempotency'
import { useMe } from '@/api/me'
import {
  useChangeRole,
  useDeactivate,
  useInvitations,
  useMemberships,
  useReactivate,
  useResendInvitation,
  useRevokeInvitation,
} from '@/api/users'
import type { InvitationItem, MembershipItem, Role } from '@/api/users'
import { useWorkspace } from '@/api/workspace'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { userErrorMessage } from '@/components/users/error-message'
import { InviteDialog } from '@/components/users/invite-dialog'

// Users & invitations (UI-SCREENS A-08, #46): members, pending and expired invites; invite,
// resend / revoke, change role, deactivate / reactivate. Owner only.
export const Route = createFileRoute('/w/$slug/settings/users')({
  component: UsersSettings,
  staticData: { breadcrumb: 'settings.users.title' },
})

// Every change except Resend is confirmed first.
type PendingAction =
  | { kind: 'role'; membership: MembershipItem; role: Role }
  | { kind: 'deactivate'; membership: MembershipItem }
  | { kind: 'reactivate'; membership: MembershipItem }
  | { kind: 'revoke'; invitation: InvitationItem }

type Outcome = { notice: string } | { error: unknown }

const displayName = (membership: MembershipItem) => membership.user.name ?? membership.user.email

function UsersSettings() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  // Already loaded by the workspace layout; Staff has no settings screens (the API refuses too).
  const workspace = useWorkspace(slug)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [pending, setPending] = useState<PendingAction | null>(null)
  const actions = useUserActions(slug, setOutcome)

  if (workspace.data?.role !== 'owner') return <Navigate to="/w/$slug" params={{ slug }} replace />

  return (
    <section className="flex max-w-3xl flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('settings.users.title')}</h1>
        <InviteDialog slug={slug} onInvited={(email) => setOutcome({ notice: t('settings.users.invited', { email }) })} />
      </div>

      <p role="status" aria-live="polite" className="text-sm empty:hidden">
        {outcome && 'notice' in outcome ? outcome.notice : ''}
      </p>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {outcome && 'error' in outcome ? userErrorMessage(t, outcome.error) : ''}
      </p>

      <MembersCard slug={slug} busy={actions.busy} onAction={setPending} />
      <InvitationsCard
        slug={slug}
        timezone={workspace.data.timezone}
        busy={actions.busy}
        onAction={setPending}
        onResend={actions.resend}
      />

      {pending ? (
        <ConfirmDialog
          action={pending}
          onCancel={() => setPending(null)}
          onConfirm={(action) => {
            setPending(null)
            actions.run(action)
          }}
        />
      ) : null}
    </section>
  )
}

// One Idempotency-Key per action and target (payload), settled after every outcome.
function useUserActions(slug: string, setOutcome: (outcome: Outcome) => void) {
  const { t } = useTranslation()
  const changeRole = useChangeRole()
  const deactivate = useDeactivate()
  const reactivate = useReactivate()
  const resendInvitation = useResendInvitation()
  const revoke = useRevokeInvitation()
  const keys = {
    role: useIdempotencyKey(),
    deactivate: useIdempotencyKey(),
    reactivate: useIdempotencyKey(),
    resend: useIdempotencyKey(),
    revoke: useIdempotencyKey(),
  }

  function handlers(key: ReturnType<typeof useIdempotencyKey>, notice: string) {
    return {
      onSuccess: () => setOutcome({ notice }),
      onError: (error: unknown) => setOutcome({ error }),
      onSettled: (_data: unknown, error: unknown) => key.settle(error),
    }
  }

  function run(action: PendingAction) {
    switch (action.kind) {
      case 'role': {
        const { membership, role } = action
        const body = { membership: { role } }
        const header = keys.role.headerFor({ id: membership.id, ...body })
        const name = displayName(membership)
        changeRole.mutate(
          { params: { path: { slug, id: membership.id }, header }, body },
          handlers(keys.role, t('settings.users.roleChanged', { name, role: t(`roles.${role}`) })),
        )
        break
      }
      case 'deactivate': {
        const { membership } = action
        const header = keys.deactivate.headerFor({ id: membership.id })
        deactivate.mutate(
          { params: { path: { slug, membership_id: membership.id }, header } },
          handlers(keys.deactivate, t('settings.users.deactivatedDone', { name: displayName(membership) })),
        )
        break
      }
      case 'reactivate': {
        const { membership } = action
        const header = keys.reactivate.headerFor({ id: membership.id })
        reactivate.mutate(
          { params: { path: { slug, membership_id: membership.id }, header } },
          handlers(keys.reactivate, t('settings.users.reactivatedDone', { name: displayName(membership) })),
        )
        break
      }
      case 'revoke': {
        const { invitation } = action
        const header = keys.revoke.headerFor({ id: invitation.id })
        revoke.mutate(
          { params: { path: { slug, invitation_id: invitation.id }, header } },
          handlers(keys.revoke, t('settings.users.revoked', { email: invitation.email })),
        )
        break
      }
    }
  }

  function resend(invitation: InvitationItem) {
    const header = keys.resend.headerFor({ id: invitation.id })
    resendInvitation.mutate(
      { params: { path: { slug, invitation_id: invitation.id }, header } },
      handlers(keys.resend, t('settings.users.resent', { email: invitation.email })),
    )
  }

  const busy =
    changeRole.isPending || deactivate.isPending || reactivate.isPending || resendInvitation.isPending || revoke.isPending

  return { run, resend, busy }
}

function MembersCard({ slug, busy, onAction }: { slug: string; busy: boolean; onAction: (action: PendingAction) => void }) {
  const { t } = useTranslation()
  const memberships = useMemberships(slug)
  const me = useMe()

  return (
    <section className="flex flex-col gap-2">
      <h2 className="font-heading text-base font-medium">{t('settings.users.members')}</h2>
      {memberships.isPending ? (
        <LoadingSkeleton />
      ) : memberships.isError || !memberships.data ? (
        <p className="text-sm text-destructive">{t('errors.generic')}</p>
      ) : (
        <RecordList>
          {memberships.data.map((membership) => (
            <MemberRow
              key={membership.id}
              membership={membership}
              isSelf={membership.user.id === me.data?.user.id}
              // The server refuses to demote or deactivate the last active Owner (4226), so
              // those actions aren't offered either.
              isLastActiveOwner={activeOwnerIds(memberships.data).join() === String(membership.id)}
              busy={busy}
              onAction={onAction}
            />
          ))}
        </RecordList>
      )}
    </section>
  )
}

const activeOwnerIds = (memberships: MembershipItem[]) =>
  memberships.filter((membership) => membership.role === 'owner' && !membership.deactivated_at).map((m) => m.id)

function MemberRow({
  membership,
  isSelf,
  isLastActiveOwner,
  busy,
  onAction,
}: {
  membership: MembershipItem
  isSelf: boolean
  isLastActiveOwner: boolean
  busy: boolean
  onAction: (action: PendingAction) => void
}) {
  const { t } = useTranslation()
  const name = displayName(membership)
  const otherRole: Role = membership.role === 'owner' ? 'staff' : 'owner'
  const { can } = membership
  // The only role change an Owner has is a demotion, so the last active Owner gets neither.
  const canChangeRole = can.update && !isLastActiveOwner
  const canDeactivate = can.deactivate && !isLastActiveOwner
  const hasActions = canChangeRole || canDeactivate || can.reactivate

  return (
    <RecordListItem
      title={
        <>
          {name}
          {isSelf ? <span className="font-normal text-muted-foreground"> ({t('settings.users.you')})</span> : null}
        </>
      }
      badges={
        <>
          <Badge variant="secondary">{t(`roles.${membership.role}`)}</Badge>
          {membership.deactivated_at ? <Badge variant="outline">{t('settings.users.deactivated')}</Badge> : null}
        </>
      }
      meta={[
        membership.user.name
          ? { icon: MailIcon, label: t('settings.users.invite.emailLabel'), value: membership.user.email }
          : null,
      ]}
      actions={
        hasActions ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" disabled={busy} aria-label={t('settings.users.actionsFor', { name })}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* A deactivated member's role is editable too: reactivation restores the role the
                  membership carries, so the Owner can correct it first (MembershipPolicy). */}
              {canChangeRole ? (
                <DropdownMenuItem onSelect={() => onAction({ kind: 'role', membership, role: otherRole })}>
                  {t(otherRole === 'owner' ? 'settings.users.makeOwner' : 'settings.users.makeStaff')}
                </DropdownMenuItem>
              ) : null}
              {canDeactivate ? (
                <DropdownMenuItem variant="destructive" onSelect={() => onAction({ kind: 'deactivate', membership })}>
                  {t('settings.users.deactivate')}
                </DropdownMenuItem>
              ) : null}
              {can.reactivate ? (
                <DropdownMenuItem onSelect={() => onAction({ kind: 'reactivate', membership })}>
                  {t('settings.users.reactivate')}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : undefined
      }
    />
  )
}

function InvitationsCard({
  slug,
  timezone,
  busy,
  onAction,
  onResend,
}: {
  slug: string
  timezone: string
  busy: boolean
  onAction: (action: PendingAction) => void
  onResend: (invitation: InvitationItem) => void
}) {
  const { t } = useTranslation()
  const invitations = useInvitations(slug)
  const formatDate = new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short', timeZone: timezone })

  return (
    <section className="flex flex-col gap-2">
      <h2 className="font-heading text-base font-medium">{t('settings.users.invitations')}</h2>
      {invitations.isPending ? (
        <LoadingSkeleton />
      ) : invitations.isError || !invitations.data ? (
        <p className="text-sm text-destructive">{t('errors.generic')}</p>
      ) : invitations.data.length === 0 ? (
        <RecordListEmpty>{t('settings.users.noInvitations')}</RecordListEmpty>
      ) : (
        <RecordList>
          {invitations.data.map((invitation) => (
            <RecordListItem
              key={invitation.id}
              title={<span id={`invitation-${invitation.id}`}>{invitation.email}</span>}
              badges={
                <>
                  <Badge variant="secondary">{t(`roles.${invitation.role}`)}</Badge>
                  {invitation.expired ? <Badge variant="destructive">{t('settings.users.expired')}</Badge> : null}
                </>
              }
              meta={[
                {
                  icon: UserIcon,
                  label: t('settings.users.list.invitedBy'),
                  value: invitation.invited_by
                    ? t('settings.users.invitedBy', { name: invitation.invited_by.name ?? invitation.invited_by.email })
                    : t('settings.users.invitedAtSetup'),
                },
                invitation.expired
                  ? null
                  : {
                      icon: ClockIcon,
                      label: t('settings.users.list.expires'),
                      value: t('settings.users.expires', { date: formatDate.format(new Date(invitation.expires_at)) }),
                    },
              ]}
              actions={
                invitation.can.resend || invitation.can.revoke ? (
                  <>
                    {invitation.can.resend ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        aria-describedby={`invitation-${invitation.id}`}
                        onClick={() => onResend(invitation)}
                      >
                        {t('settings.users.resend')}
                      </Button>
                    ) : null}
                    {invitation.can.revoke ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        disabled={busy}
                        aria-describedby={`invitation-${invitation.id}`}
                        onClick={() => onAction({ kind: 'revoke', invitation })}
                      >
                        {t('settings.users.revoke')}
                      </Button>
                    ) : null}
                  </>
                ) : undefined
              }
            />
          ))}
        </RecordList>
      )}
    </section>
  )
}

// Mounted only while something is pending, so its copy is never half-empty: the parent clears
// `pending` as soon as the action is confirmed or cancelled.
function ConfirmDialog({
  action,
  onCancel,
  onConfirm,
}: {
  action: PendingAction
  onCancel: () => void
  onConfirm: (action: PendingAction) => void
}) {
  const { t } = useTranslation()
  const me = useMe()
  const isSelf = action.kind !== 'revoke' && action.membership.user.id === me.data?.user.id
  const copy = confirmCopy(t, action, isSelf)

  return (
    <AlertDialog open onOpenChange={(open) => (open ? undefined : onCancel())}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          <AlertDialogDescription>{copy.body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="h-11">{t('settings.users.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            className="h-11"
            variant={copy.destructive ? 'destructive' : 'default'}
            onClick={() => onConfirm(action)}
          >
            {t('settings.users.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function confirmCopy(
  t: ReturnType<typeof useTranslation>['t'],
  action: PendingAction,
  isSelf: boolean,
): { title: string; body: string; destructive: boolean } {
  switch (action.kind) {
    case 'role': {
      const name = displayName(action.membership)
      const role = t(`roles.${action.role}`)
      // An Owner demoting themself loses the settings, this screen included (#46).
      if (isSelf && action.role === 'staff') {
        return { title: t('settings.users.confirmSelfDemoteTitle'), body: t('settings.users.confirmSelfDemoteBody'), destructive: true }
      }
      return {
        title: t('settings.users.confirmRoleTitle', { name, role }),
        body: t('settings.users.confirmRoleBody', { name, role }),
        destructive: false,
      }
    }
    case 'deactivate': {
      const name = displayName(action.membership)
      // An Owner deactivating themself is told what happens to them, not to "that member".
      if (isSelf) {
        return {
          title: t('settings.users.confirmSelfDeactivateTitle'),
          body: t('settings.users.confirmSelfDeactivateBody'),
          destructive: true,
        }
      }
      return {
        title: t('settings.users.confirmDeactivateTitle', { name }),
        body: t('settings.users.confirmDeactivateBody', { name }),
        destructive: true,
      }
    }
    case 'reactivate': {
      const name = displayName(action.membership)
      return {
        title: t('settings.users.confirmReactivateTitle', { name }),
        body: t('settings.users.confirmReactivateBody', { name, role: t(`roles.${action.membership.role}`) }),
        destructive: false,
      }
    }
    case 'revoke':
      return {
        title: t('settings.users.confirmRevokeTitle', { email: action.invitation.email }),
        body: t('settings.users.confirmRevokeBody'),
        destructive: true,
      }
  }
}
